import { Stack, Typography } from '@mui/material';
import { PATH_DASHBOARD } from 'src/Router/paths';
import Iconify from 'src/components/iconify';
import { UploadBox } from 'src/components/upload';
import { UploadDropzone } from 'src/components/upload/UploadDropzone';
import { NavAccordionSummary } from 'src/features/Navbar/NavAccordion';
import { NavAccordionLabel } from 'src/features/Navbar/NavAccordion/NavAccordion';
import { NavAccordionPersisted } from 'src/features/Navbar/NavAccordionPersisted';
import { NavActionButton } from 'src/features/Navbar/NavActionButton/NavActionButton';
import { useLocales } from 'src/locales';
import { useFileUploadContext } from 'src/provider/FileUploadContext';
import { useFileUploadDialog } from 'src/sections/@dashboard/drive/portal/UploadFilesDialog';
import useAddFileMutation from 'src/sections/@dashboard/drive/useAddFileMutation';
import { useDriveInfoQuery } from 'src/sections/@dashboard/drive/useDriveInfoQuery';
import { UIFeature } from 'src/state/ui/types';
import { NavRootList } from '../../shared/styles';
import { DriveNavItem } from './DriveNavItem';
import { DriveStorageCounter } from './DriveStorageCounter';

export type NavDriveActionsList = {
  icon: string;
  name: string;
  action?: string;
  divider: boolean;
};

export const NavDrive = () => {
  const { translate } = useLocales();

  const { driveInfo } = useDriveInfoQuery();

  const { addFile } = useAddFileMutation('');

  const { showFileUpload } = useFileUploadDialog();

  const { control } = useFileUploadContext();

  const handleUploadFiles = async (files: File[]) => {
    const filesWithId = control.addFiles(files);

    await Promise.all(
      filesWithId.map(async (file) => {
        await addFile({
          file: file.file,
          onUploadProgress({ progress, speed }) {
            control.updateFile({
              ...file,
              progress,
              speed,
            });
          },
        })
          .then((res) => {
            control.updateFile({
              ...file,
              isCompleted: true,
            });
            return res;
          })
          .catch(() => {
            control.removeFiles([file]);
          });
      }),
    );

    control.removeFiles(filesWithId);
  };

  const NavStoreActions: NavDriveActionsList[] = [
    {
      icon: 'tabler:cloud',
      name: translate('header.nav_drive.my_drive'),
      action: PATH_DASHBOARD.drive.root,
      divider: false,
    },
    {
      icon: 'tabler:user',
      name: translate('header.nav_drive.shared_with_me'),
      action: PATH_DASHBOARD.drive.shared,
      divider: false,
    },
    {
      icon: 'tabler:star',
      name: translate('header.nav_drive.star'),
      action: PATH_DASHBOARD.drive.starred,
      divider: true,
    },
    {
      icon: 'tabler:trash',
      name: translate('header.nav_drive.trash'),
      action: PATH_DASHBOARD.drive.trash,
      divider: false,
    },
  ];

  return (
    <NavAccordionPersisted feature={UIFeature.drive}>
      <NavAccordionSummary className="drive">
        <NavAccordionLabel>
          <Iconify className="navIcon" icon="tabler:cloud-filled" />
          <Typography variant="subtitle2" whiteSpace="nowrap">
            {translate('header.nav_vertical.drive')}
          </Typography>
        </NavAccordionLabel>
        <NavActionButton
          icon="tabler:plus"
          onClick={(e) => {
            showFileUpload(handleUploadFiles);
            e.stopPropagation();
          }}
        />
      </NavAccordionSummary>
      <NavRootList disablePadding>
        {NavStoreActions.map((navItem) => (
          <DriveNavItem key={navItem.name} driveItem={navItem} />
        ))}
      </NavRootList>
      {driveInfo && <DriveStorageCounter driveInfo={driveInfo} />}
      <Stack sx={{ padding: 1.5, mb: 2.5 }}>
        <UploadDropzone onDrop={handleUploadFiles}>
          <UploadBox
            sx={{
              width: '100%',
              height: 70,
              boxSizing: 'border-box',
            }}
            onDrop={handleUploadFiles}
          />
        </UploadDropzone>
      </Stack>
    </NavAccordionPersisted>
  );
};
